import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';
import { ProductDemo } from '../../models/product-demo.model';
import { WorkflowDemo } from '../../models/workflow-demo.model';
import { UseCase } from '../../models/use-case.model';
import { ProductDemosService } from '../../services/product-demos.service';
import { WorkflowDemosService } from '../../services/workflow-demos.service';
import { UseCasesService } from '../../services/use-cases.service';
import { getYouTubeThumbnail } from '../../utils/youtube.utils';
import { PaginatedSectionBase } from '../shared/paginated-section.base';
import { SectionShellComponent } from '../shared/section-shell/section-shell.component';

export type BookmarkItem =
  | { kind: 'productDemo'; item: ProductDemo }
  | { kind: 'workflowDemo'; item: WorkflowDemo }
  | { kind: 'useCase'; item: UseCase };

@Component({
  selector: 'app-bookmarks-collection',
  standalone: true,
  imports: [CommonModule, SectionShellComponent],
  templateUrl: './bookmarks-collection.component.html',
  styleUrl: './bookmarks-collection.component.scss'
})
export class BookmarksCollectionComponent extends PaginatedSectionBase<BookmarkItem> implements OnInit, OnChanges {
  @Input() bookmarkedProductDemoIds: number[] = [];
  @Input() bookmarkedWorkflowDemoIds: number[] = [];
  @Input() bookmarkedUseCaseIds: number[] = [];
  @Output() toggleProductDemo = new EventEmitter<number>();
  @Output() toggleWorkflowDemo = new EventEmitter<number>();
  @Output() toggleUseCase = new EventEmitter<number>();

  private allProductDemos: ProductDemo[] = [];
  private allWorkflowDemos: WorkflowDemo[] = [];
  private allUseCases: UseCase[] = [];

  private readonly productDemosService = inject(ProductDemosService);
  private readonly workflowDemosService = inject(WorkflowDemosService);
  private readonly useCasesService = inject(UseCasesService);

  get hasBookmarks(): boolean {
    return this.filteredItems.length > 0;
  }

  get visibleBookmarks(): BookmarkItem[] {
    return this.visibleItems;
  }

  ngOnInit(): void {
    combineLatest([
      this.productDemosService.getProductDemos(),
      this.workflowDemosService.getWorkflowDemos(),
      this.useCasesService.getUseCases()
    ]).pipe(take(1)).subscribe(([productDemos, workflowDemos, useCases]) => {
      this.allProductDemos = productDemos;
      this.allWorkflowDemos = workflowDemos;
      this.allUseCases = useCases;
      this.filterBookmarks();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['bookmarkedProductDemoIds'] || changes['bookmarkedWorkflowDemoIds'] || changes['bookmarkedUseCaseIds']) {
      this.filterBookmarks();
    }
  }

  private filterBookmarks(): void {
    const items: BookmarkItem[] = [
      ...this.allProductDemos
        .filter(d => this.bookmarkedProductDemoIds.includes(d.id))
        .map(item => ({ kind: 'productDemo' as const, item })),
      ...this.allWorkflowDemos
        .filter(d => this.bookmarkedWorkflowDemoIds.includes(d.id))
        .map(item => ({ kind: 'workflowDemo' as const, item })),
      ...this.allUseCases
        .filter(u => this.bookmarkedUseCaseIds.includes(u.id))
        .map(item => ({ kind: 'useCase' as const, item })),
    ];
    this.setFilteredItems(items);
  }

  isProductDemo(entry: BookmarkItem): entry is { kind: 'productDemo'; item: ProductDemo } {
    return entry.kind === 'productDemo';
  }

  isWorkflowDemo(entry: BookmarkItem): entry is { kind: 'workflowDemo'; item: WorkflowDemo } {
    return entry.kind === 'workflowDemo';
  }

  isUseCase(entry: BookmarkItem): entry is { kind: 'useCase'; item: UseCase } {
    return entry.kind === 'useCase';
  }

  getThumbnail(videoUrl: string): string {
    return getYouTubeThumbnail(videoUrl);
  }
}
